// Single source of truth for the dataset catalogue and subscription tiers.
// Built from the canonical 10-category CSV taxonomy (primary_category).
// Compliance: records are privacy-safe DERIVED patterns (knowledge_source =
// SMS_DERIVED_PATTERN / COMBINED_PATTERN) — NOT raw messages or real PII.
// Performance is not claimed; high-risk records are flagged requires_human_review.

export const CURRENCY = "A$";

// Shared decision schema across every dataset.
export const SCHEMA_FIELDS = [
  "record_id", "scenario", "contact_message", "booking_history",
  "risk_level", "decision", "recommended_action", "example_response",
  "reasoning", "requires_human_review", "knowledge_source",
];

// 10 intent-specific agent-skill datasets, ~100 labelled records each.
export const DATASETS = [
  {
    id: "initial_booking_enquiry",
    index: "01",
    name: "Initial Booking Enquiry",
    kicker: "Enquiry Qualification",
    tagline: "Turning vague first-contact messages into complete, actionable booking requests.",
    description:
      "Labelled examples for handling first-contact enquiries — detecting missing details and asking for the minimum booking information in one clear message before anything proceeds.",
    scenarios: ["Greeting, no details", "Partial enquiry", "Request-for-info"],
    records: 99,
    riskRange: "LOW → MED",
    span: "lg:col-span-8",
  },
  {
    id: "screening_and_reliability",
    index: "02",
    name: "Screening & Reliability",
    kicker: "Client Vetting",
    tagline: "Reading reliability signals and screening clients before committing time.",
    description:
      "Decision records for vetting new and returning contacts — weighing booking history and reliability signals into a clear screen / proceed / decline recommendation.",
    scenarios: ["New contact", "Reliability signals", "Repeat screening"],
    records: 99,
    riskRange: "LOW → HIGH",
    span: "lg:col-span-4",
  },
  {
    id: "payment_and_commitment",
    index: "03",
    name: "Payment & Commitment",
    kicker: "Deposits & Rates",
    tagline: "Handling rates, deposits and commitment confirmation conversations.",
    description:
      "Labelled examples for rate questions, deposit requests and confirming genuine commitment — with recommended responses that hold terms without burning rapport.",
    scenarios: ["Rate enquiry", "Deposit request", "Commitment check"],
    records: 99,
    riskRange: "LOW → MED",
    span: "lg:col-span-4",
  },
  {
    id: "confirmed_booking_logistics",
    index: "04",
    name: "Confirmed Booking Logistics",
    kicker: "Coordination",
    tagline: "Coordinating time, place and logistics once a booking is confirmed.",
    description:
      "Decision records for the confirmed-booking phase — managing timing, directions and last-mile coordination in a privacy-aware way.",
    scenarios: ["Timing", "Directions", "Day-of coordination"],
    records: 99,
    riskRange: "LOW → MED",
    span: "lg:col-span-4",
  },
  {
    id: "cancellation_and_noncompletion",
    index: "05",
    name: "Cancellation & Non-Completion",
    kicker: "Friction & Recovery",
    tagline: "Managing cancellations, lateness and no-shows without losing control.",
    description:
      "Labelled examples for cancellations, late arrivals and non-completion — applying deposit and notice logic while keeping the option to recover the booking.",
    scenarios: ["Late cancel", "No-show", "Reschedule"],
    records: 100,
    riskRange: "LOW → HIGH",
    span: "lg:col-span-4",
  },
  {
    id: "service_and_consent_boundaries",
    index: "06",
    name: "Service & Consent Boundaries",
    kicker: "Boundary Enforcement",
    tagline: "Interpreting service questions while firmly holding consent boundaries.",
    description:
      "Decision records for mapping service preferences and enforcing consent boundaries — a core skill for any respectful, worker-first agent.",
    scenarios: ["Preference capture", "Boundary push", "Respectful decline"],
    records: 99,
    riskRange: "LOW → HIGH",
    span: "lg:col-span-4",
  },
  {
    id: "harassment_and_contact_safety",
    index: "07",
    name: "Harassment & Contact Safety",
    kicker: "Safety Red-Lines",
    tagline: "Detecting harassment and block-evasion, and shutting it down safely.",
    description:
      "High-signal decision records for harassment, boundary-crossing and blocked-contact evasion — labelled to stop the conversation and route to the safety procedure rather than re-engage.",
    scenarios: ["Harassment", "Block evasion", "Threatening contact"],
    records: 99,
    riskRange: "MED → HIGH",
    span: "lg:col-span-8",
  },
  {
    id: "health_substance_impairment_risk",
    index: "08",
    name: "Health, Substance & Impairment Risk",
    kicker: "Risk Triage",
    tagline: "Triaging health, substance and impairment signals before they become incidents.",
    description:
      "Decision records for recognising health, substance and impairment risk in messages — with recommended actions that prioritise safety and human review.",
    scenarios: ["Impairment signals", "Substance mention", "Health risk"],
    records: 99,
    riskRange: "MED → HIGH",
    span: "lg:col-span-4",
  },
  {
    id: "personal_and_relationship_routing",
    index: "09",
    name: "Personal & Relationship Routing",
    kicker: "Intent Routing",
    tagline: "Routing personal, emotional or relationship-style messages appropriately.",
    description:
      "Labelled examples for messages that blur personal and professional lines — routing them to the right response path without over-automating sensitive moments.",
    scenarios: ["Personal message", "Emotional contact", "Relationship framing"],
    records: 99,
    riskRange: "LOW → MED",
    span: "lg:col-span-4",
  },
  {
    id: "automated_and_administrative_routing",
    index: "10",
    name: "Automated & Administrative Routing",
    kicker: "Ops & Spam",
    tagline: "Separating spam, automation and admin noise from real enquiries.",
    description:
      "Decision records for automated, administrative and spam traffic — classifying and routing non-genuine contact so human attention stays on real bookings.",
    scenarios: ["Spam", "Automation", "Admin noise"],
    records: 99,
    riskRange: "LOW → MED",
    span: "lg:col-span-4",
  },
];

// Subscription tiers (AUD, billed annually on both 1-year and 2-year commitments).
// datasetCount: how many datasets the buyer must pick (null = all).
export const TIERS = [
  {
    id: "single", name: "Single Stream", datasetCount: 1,
    blurb: "Pick one dataset and build deep on a single skill.",
    monthly: { "1yr": 29.99, "2yr": 14.99 },
    lookup: { "1yr": "single_1yr", "2yr": "single_2yr" },
    features: ["Choose any 1 dataset", "Monthly update cycle", "Prompt-ready CSV + manifest", "Commercial development license"],
  },
  {
    id: "dual", name: "Dual Stream", datasetCount: 2,
    blurb: "Combine two skills — e.g. enquiries + screening.",
    monthly: { "1yr": 49.99, "2yr": 34.99 },
    lookup: { "1yr": "dual_1yr", "2yr": "dual_2yr" },
    features: ["Choose any 2 datasets", "Monthly update cycle", "Prompt-ready CSV + manifest", "Priority support"],
  },
  {
    id: "tri", name: "Tri-Core", datasetCount: 3,
    blurb: "Three datasets for a well-rounded agent stack.",
    monthly: { "1yr": 89.99, "2yr": 74.99 },
    lookup: { "1yr": "tri_1yr", "2yr": "tri_2yr" },
    features: ["Choose any 3 datasets", "Monthly update cycle", "Prompt-ready CSV + manifest", "Priority support"],
    featured: true,
  },
  {
    id: "allaccess", name: "Enterprise All Access", datasetCount: null,
    blurb: "All ten datasets plus every monthly update, one license.",
    monthly: { "1yr": 249.99, "2yr": 234.99 },
    lookup: { "1yr": "allaccess_1yr", "2yr": "allaccess_2yr" },
    features: ["All 10 datasets included", "Monthly update cycle", "Prompt-ready CSV + manifest", "Dedicated onboarding"],
  },
];
